import { Trans } from 'react-i18next'

import CopyIcon from '@/assets/icons/copy.svg?react'
import { Button, Divider } from '@/components'
import { mapRole } from '@/pages/teams/member/adapters/teamMember'

import { NewMemberResult } from '../types/types'

export function AddMemberResultView({
  data,
  onClose,
  onCopyAll,
}: {
  data: NewMemberResult
  onClose: () => void
  onCopyAll: () => void
}) {
  const role = mapRole(data.role)
  return (
    <>
      <p className="t-sb mb-4 text-primary">
        <Trans i18nKey="team.member.resultView.newMemberCreatedTip">
          The new member has been added successfully. Please share the login details below.
        </Trans>
      </p>

      <div className="rounded-xl bg-neutral-50 p-4 text-primary space-y-4">
        <div className="flex gap-2 items-center">
          <div className="t-xs text-tertiary flex-1 text-nowrap">
            <Trans i18nKey="team.member.resultView.basicInformation">Basic Information</Trans>
          </div>
          <Divider className="my-0" />
        </div>
        <div className="grid grid-cols-2 gap-y-4 gap-x-8">
          <div>
            <div className="t-xs text-secondary mb-1">
              <Trans i18nKey="team.member.resultView.memberId">Member ID</Trans>
            </div>
            <div className="t-sb">{data.id}</div>
          </div>
          <div>
            <div className="t-xs text-secondary mb-1">
              <Trans i18nKey="team.member.resultView.account">Account</Trans>
            </div>
            <div className="t-sb">{data.accountUserName}</div>
          </div>
          <div>
            <div className="t-xs text-secondary mb-1">
              <Trans i18nKey="team.member.resultView.role">Role</Trans>
            </div>
            <div className="t-sb">{role}</div>
          </div>
          <div>
            <div className="t-xs text-secondary mb-1">
              <Trans i18nKey="team.member.resultView.credits">Credits</Trans>
            </div>
            <div className="t-sb">{data.credits}</div>
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <div className="t-xs text-tertiary flex-1 text-nowrap">
            <Trans i18nKey="team.member.resultView.loginInformation">Login Information</Trans>
          </div>
          <Divider className="my-0" />
        </div>

        <div className="grid grid-cols-2 gap-y-4 gap-x-8">
          <div>
            <div className="t-xs text-secondary mb-1">
              <Trans i18nKey="team.member.resultView.loginAccount">Login Account</Trans>
            </div>
            <div className="t-sb">{data.account}</div>
          </div>
          <div>
            <div className="t-xs text-secondary mb-1">
              <Trans i18nKey="team.member.resultView.loginPassword">Login Password</Trans>
            </div>
            <div className="t-sb flex items-center gap-2">
              {data.password}
              <button
                type="button"
                className="t-sm text-highlight"
                onClick={() => navigator.clipboard.writeText(data.password)}
              >
                <CopyIcon width={16} height={16} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <div className="t-xs text-tertiary flex-1 text-nowrap">
            <Trans i18nKey="team.member.resultView.loginTips">Login Tips</Trans>
          </div>
          <Divider className="my-0" />
        </div>
        <ul className="t-sb list-disc list-inside pl-2 text-primary">
          <li>
            <Trans
              i18nKey="team.member.resultView.linkAccountTip"
              components={[<span className="text-highlight" />]}
            />
          </li>
          <li>
            <Trans
              i18nKey="team.member.resultView.directLoginTip"
              components={[
                <a
                  className="text-highlight"
                  href="https://platform.glows.ai/team/login"
                  target="_blank"
                  rel="noopener noreferrer"
                />,
              ]}
            />
          </li>
        </ul>
      </div>

      <div className="pt-6 flex items-center justify-end gap-4">
        <Button type="outline" className="min-w-[96px]" onClick={onClose}>
          <Trans i18nKey="common.cancel">Cancel</Trans>
        </Button>
        <Button type="secondary" className="min-w-[180px]" onClick={onCopyAll}>
          <Trans i18nKey="team.member.copyLoginDetails">Copy Login Details</Trans>
        </Button>
      </div>
    </>
  )
}
